/**
 * Generate Text tenant-facing failure copy — presentation only.
 * Raw errors stay on task/run records and in node `errorMessage` for diagnostics.
 */

/** i18n key — zh-CN: 「生成失败」 */
export const GENERATE_TEXT_TENANT_FAILURE_I18N_KEY = 'generateText.tenantFailure'

/** i18n key — zh-CN: 「结果解析失败」 (VistoeraTASK-00662) */
export const GENERATE_TEXT_RESULT_PARSE_FAILURE_I18N_KEY = 'generateText.resultParseFailed'

const RESULT_PARSE_ERROR_CODES = new Set([
  'SCHEMA_VALIDATION_FAILED',
  'PROVIDER_INVALID_JSON',
  'PROVIDER_SCHEMA_MISMATCH',
  'STRUCTURED_PARSE_FAILED',
  'STRUCTURED_RESULT_INVALID',
])

/**
 * Resolve tenant-safe failure i18n key from run status / error code.
 * Never returns raw provider / validator text.
 */
export function resolveGenerateTextTenantFailureI18nKey(
  errorCode: string | null | undefined,
): string {
  const code = String(errorCode || '')
    .trim()
    .toUpperCase()
  if (RESULT_PARSE_ERROR_CODES.has(code)) {
    return GENERATE_TEXT_RESULT_PARSE_FAILURE_I18N_KEY
  }
  return GENERATE_TEXT_TENANT_FAILURE_I18N_KEY
}

/**
 * Whether the Generate Text result area should show the tenant failure banner.
 * Uses run status only; never surfaces `fields.errorMessage` to tenants.
 */
export function shouldShowGenerateTextTenantFailureBanner(
  runStatus: string | null | undefined,
  isBusy: boolean,
): boolean {
  if (isBusy) return false
  return String(runStatus || '').toUpperCase() === 'ERROR'
}

/**
 * Pure guard for tests: tenant-visible failure text must never echo raw technical errors.
 */
export function assertGenerateTextTenantFailureNeverEchoesRaw(
  tenantVisibleText: string,
  rawErrorMessage: string | null | undefined,
): boolean {
  const raw = typeof rawErrorMessage === 'string' ? rawErrorMessage.trim() : ''
  if (!raw) return true
  if (tenantVisibleText === raw) return false
  if (tenantVisibleText.includes(raw)) return false
  const lower = tenantVisibleText.toLowerCase()
  const forbidden = [
    'browserType',
    'playwright',
    'launchPersistentContext',
    'content-policy',
    'provider returned',
    '/users/',
    '/library/caches/',
  ]
  return !forbidden.some((f) => lower.includes(f))
}
